import {useRequiredConfig} from '@domain/shifthandover'
import {useInstructionsPageLastVisited} from '@features/instructions-and-documents'
import {WhiteList} from '@hconnect/common/hproduce'
import {Env} from '@hconnect/common/hproduce/types'
import {
  Notifications,
  TranslationKey,
  TranslationActionKey,
  Translations,
  NotificationsOptions,
  generateNotificationChannels
} from '@hconnect/common/notifications'
import {getSupportedLanguages} from '@hconnect/common/utils'
import {useRequiredUser} from '@hconnect/login'
import {CommonHeader, useGuidedTours} from '@hconnect/uikit/src/lib2'
import {getPlantName, useUpmPlantsContext, useUpmPlantTimezone} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {
  matchPath,
  generatePath,
  useNavigate,
  useLocation,
  useSearchParams,
  SetURLSearchParams
} from 'react-router'

import {useTrackAnalyticsEvent} from '../../hooks/useTrackAnalyticsEvents'
import {links, links as linksList, extendedLinks, routeToDocuments} from '../../routes'

export type Props = {
  onLogout?: () => void
  whiteList: WhiteList
  plantId: string
}

const NOTIFICATIONS_TRANSLATION_KEYS: Translations = {
  label: {
    [TranslationKey.TODAY]: 'notifications.label.today',
    [TranslationKey.YESTERDAY]: 'notifications.label.yesterday',
    [TranslationKey.NOTIFICATIONS]: 'notifications.label.notifications',
    [TranslationKey.EMPTY]: 'notifications.label.empty',
    [TranslationKey.ERROR]: 'error.label.fetchingError',
    [TranslationKey.SHOULD_DELETE]: 'notifications.label.shouldDeleteNotification'
  },
  action: {
    [TranslationActionKey.DELETE]: 'notifications.action.delete',
    [TranslationActionKey.CONFIRM_DELETE]: 'notifications.action.confirmDelete',
    [TranslationActionKey.CANCEL]: 'action.cancel'
  }
}

export const HeaderContent: React.FC<Props> = (props) => {
  const {t, i18n} = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useRequiredUser()
  const [queryParameters]: [URLSearchParams, SetURLSearchParams] = useSearchParams()
  const config = useRequiredConfig(props.plantId)
  const trackAnalyticsEvent = useTrackAnalyticsEvent({
    plantId: props.plantId,
    country: config.country
  })
  const notificationsFeatureFlagEnabled = useCommonFeatureFlag(FeatureFlagKey.Notifications)
  const notificationsEnabled = notificationsFeatureFlagEnabled && props.onLogout && user
  const labQualityResultFeatureFlagEnabled = useCommonFeatureFlag(
    FeatureFlagKey.LabQualityResultPage,
    {plantId: props.plantId}
  )

  const selectedPlant = props.plantId
  const timezone = useUpmPlantTimezone(selectedPlant)

  const upmPlants = useUpmPlantsContext()

  const {hasUpdated: haveInstructionsUpdated} = useInstructionsPageLastVisited()

  const navItems = useMemo(() => {
    const inputLinksList = labQualityResultFeatureFlagEnabled ? extendedLinks : linksList
    return inputLinksList.map((link) => {
      const newURLSearchParams = new URLSearchParams()
      link.keepURLParams?.forEach((urlParam) => {
        const value = queryParameters.get(urlParam)
        if (value) {
          newURLSearchParams.append(urlParam, value)
        }
      })
      const params = newURLSearchParams.toString()
      const path = generatePath(link.to, {plantId: selectedPlant})

      return {
        label: t(link.translationKey),
        url: params ? `${path}?${params}` : path,
        showBadge:
          link.to.includes('documents') &&
          haveInstructionsUpdated &&
          generatePath(routeToDocuments, {plantId: selectedPlant}) !== location.pathname
      }
    })
  }, [
    selectedPlant,
    t,
    haveInstructionsUpdated,
    queryParameters,
    location,
    labQualityResultFeatureFlagEnabled
  ])

  const options = useMemo<NotificationsOptions>(() => {
    const channels = generateNotificationChannels(props.whiteList)
    return {
      channels,
      countryCode: config.country
    }
  }, [props.whiteList, config.country])

  const {tours, tourStatuses} = useGuidedTours()

  const onNavigate = (url: string) => {
    const currentLocationKey = links.find((link) =>
      matchPath(link.to, location.pathname)
    )?.translationKey
    const nextLocationKey = links.find((link) => matchPath(link.to, url))?.translationKey
    trackAnalyticsEvent('userMovesPage', {
      pageFrom: currentLocationKey && t(currentLocationKey, {lng: 'en'}),
      pageTo: nextLocationKey && t(nextLocationKey, {lng: 'en'})
    })
    void navigate(url)
  }

  return (
    <CommonHeader
      additionalActions={
        notificationsEnabled
          ? [
              <Notifications
                key="notification-provider"
                options={options}
                translations={NOTIFICATIONS_TRANSLATION_KEYS}
                timezone={timezone}
              />
            ]
          : undefined
      }
      data-test-id="header-content"
      appName="Cockpit"
      plant={{plantId: selectedPlant, plantName: getPlantName(upmPlants, selectedPlant)}}
      upmPlants={upmPlants}
      env={process.env.REACT_APP_STAGE as Env}
      whiteList={props.whiteList}
      navItems={navItems}
      supportedLanguages={getSupportedLanguages(i18n)}
      user={{...user, name: user.name ?? undefined}}
      onLogout={props.onLogout}
      navigate={onNavigate}
      locationPath={location.pathname}
      enableMyTasks
      guidedTours={{tours, statuses: tourStatuses}}
      closeDropdownOnLanguageSwitch
    />
  )
}
